<template>
  <sticky-header-layout>
    <!-- Header -->
    <template #header>
      <div class="action-container justify-content-end">
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            size="mini"
            type="warning"
            icon="el-icon-close"
            @click="handleCancel"
          >{{ $t('button.cancel') }}</el-button>
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            size="mini"
            type="danger"
            icon="el-icon-upload2"
            @click="handleSave"
          >{{ $t('button.save') }}</el-button>
        </span>
      </div>
    </template>
    <!-- Header -->

    <div v-loading="isLoading">
      <generic-form
        ref="form"
        :attributes="formAttributes"
        :form="form"
        :form-template="formTemplate"
      />
    </div>
  </sticky-header-layout>
</template>

<script>
import GenericForm from '../../components/form/Form.vue'
import { baseLanguages } from '../../utils/const'
import { countryList, FormRules } from '../../utils/form'

import ShopTheLookService from '../../api/shop-the-look'

const titleToSlug = title => title.split(' ').join('-').toLowerCase()

export default {
  name: 'ShopTheLookDetail',
  components: {
    GenericForm,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const formTemplate = [
      { key: 'title', isLocalized: true, default: '' },
      { key: 'description', isLocalized: true, default: '' },
      { key: 'basicInfo', isTitle: true },
      {
        key: 'goxippost_id',
        isInput: true,
        title: this.$t('table.postId'),
        default: '',
        rules: [FormRules.id],
      },
      { key: 'slug', title: this.$t('table.slug'), isInput: true, default: '' },
      {
        key: 'country',
        isSelect: true,
        list: countryList.map(country => ({ value: country.toUpperCase(), label: country.toUpperCase() })),
      },
      { key: 'sort', title: this.$t('table.sort'), isInput: true, default: '' },
      {
        key: 'status',
        isSwitch: true,
        default: false,
        title: this.$t('table.enable'),
      },
      { key: 'is_show_name', isSwitch: true, default: false },
    ]
    const form = formTemplate.reduce((acc, col) => {
      if (col.isLocalized) {
        baseLanguages.forEach((lang) => {
          acc[`${col.key}${lang.suffix}`] = col.default
        })
      } else {
        acc[col.key] = col.default
      }
      return acc
    }, {})
    return {
      isEdited: false,
      isLoading: false,
      isSaving: false,
      formTemplate,
      form,
    }
  },
  computed: {
    isEditMode() {
      return !!this.id
    },
    formAttributes() {
      return {
        disabled: this.isSaving,
        labelWidth: '124px',
      }
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
    'form.title': function _(current, prev) {
      if (!this.isEditMode && this.form.slug === titleToSlug(prev)) {
        this.form.slug = titleToSlug(current)
      }
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getDetail(this.id)
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }
    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    goToListPage() {
      this.$router.push({ path: '/shop-the-look/list' })
    },
    handleCancel() {
      this.goToListPage()
    },
    handleSave() {
      this.$refs.form.$refs.form.validate().then(() => {
        const form = {
          ...this.form,
          status: Number(this.form.status),
          is_show_name: Number(this.form.is_show_name),
        }
        if (this.isEditMode) {
          this.editShopTheLook(form, this.id)
        } else {
          this.addShopTheLook(form)
        }
      }).catch((error) => {
        console.error(error);
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), this.$t('common.oops'), {
          confirmButtonText: this.$t('button.ok'),
        });
      })
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToListPage()
    },
    handleSaveFail(error) {
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: this.$t('button.ok'),
      })
    },
    async getDetail(id) {
      this.isLoading = true
      try {
        const { data } = await ShopTheLookService.getDetail(id)
        data.status = !!data.status
        data.is_show_name = !!data.is_show_name
        this.form = data
        this.$nextTick(() => {
          this.isEdited = false
        })
      } catch (error) {
        console.error(error);
        this.$alert(this.$t('error.shopTheLookNotFound'), this.$t('common.error'), {
          type: 'error',
          confirmButtonText: this.$t('button.ok'),
        }).then(() => {
          this.goToListPage()
        })
      } finally {
        this.isLoading = false
      }
    },
    async editShopTheLook(form) {
      this.isSaving = true
      try {
        await ShopTheLookService.updateDetail(form, this.id)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async addShopTheLook(form) {
      this.isSaving = true
      try {
        await ShopTheLookService.create(form)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/form.scss";

.el-form-item {
  padding-bottom: 6px;
}

.table-img {
  width: unset;
}

::v-deep {
  .el-form-item__label {
    + .el-form-item__content {
      > *:not(.el-input):not(.el-textarea):not(.multiselect) {
        margin-top: 4px;
      }
    }
  }
  .el-table .el-table__row td {
    border: none;
    vertical-align: top;
  }
  .el-select__input.is-mini {
    height: auto;
  }
}
</style>
